import { MAX_SLIMES } from "../../engine/core/constants";
import type { GameContext } from "../../engine/core/GameContext";

/**
 * per-slime 的压力状态。由压力（涨与漏）与爆浆（临界与传染）两个 System 共享，
 * 因此不属于任何一方私有——两者对同一份体积做不同的解释。
 *
 * 不进 `SlimeStore`：按分层判据，下一次玩法推翻时体积与待爆会被删掉，而槽位、位置、
 * 半径不会。半径是唯一例外，它被物理与碰撞消费，因此由压力机制每步写回 Store。
 */
export class PressureState {
  /** 当前体积。0 表示槽位尚未初始化——静止体积恒大于 0，因此不会混淆。 */
  readonly volume = new Float32Array(MAX_SLIMES);
  /** 是否已到临界、正在等待同环窗口。待爆期间绷在极限不漏水。 */
  readonly pending = new Uint8Array(MAX_SLIMES);
  /**
   * 到达临界时的固定步序。用步计数而非秒：`state.steps` 本就存在，且回放时不受
   * 显示帧率影响——窗口判定必须在任何机器上给出相同的分环结果。
   */
  readonly pendingAt = new Float32Array(MAX_SLIMES);
  /**
   * 把它推过临界的那一只（槽位）。-1 表示由玩家灌注致爆，没有引爆者。
   * 在溅射时记录而不是在爆开时推算——爆开时它自己的压力已经到顶，看不出是谁送的最后一程。
   * 支撑平均连锁长度的重建，决策 0016 把「无法复现自限连锁」列为重新评估条件。
   */
  readonly detonator = new Int32Array(MAX_SLIMES).fill(-1);
  /** 上次见到的槽位世代。世代变化即槽位换了占用者，需要重新初始化。 */
  private readonly generation = new Uint16Array(MAX_SLIMES);

  clear(): void { this.volume.fill(0); this.pending.fill(0); this.pendingAt.fill(0); this.detonator.fill(-1); this.generation.fill(0); }

  /** 静止体积：漏水的下限，也是新生成个体的初始值。 */
  restVolume(context: GameContext, id: number): number {
    return (
      context.catalog.slimes[context.slimes.definition[id]].burstVolume *
      context.level.world.baseFraction
    );
  }

  /** 槽位换人时重置。每步开头调用，避免依赖生成与回收的生命周期钩子。 */
  syncSlots(context: GameContext): void {
    const { slimes } = context;
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (slimes.active[id] === 0) continue;
      if (
        this.generation[id] === slimes.generation[id] &&
        this.volume[id] !== 0
      )
        continue;
      this.generation[id] = slimes.generation[id];
      this.volume[id] = this.restVolume(context, id);
      this.pending[id] = 0;
      this.pendingAt[id] = 0;
      this.detonator[id] = -1;
    }
  }
}
